import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "../Common/AppOverlay";
import "./InspectionJourney.scss";
import { startGuidedTour } from "../Help/GuidedTour";

const cycleTourSteps = [
  {
    target: ".inspection-cycle-page > .summary-cards",
    title: "Check cycle status",
    description:
      "These cards show your cycle totals and status breakdown. Select a card to filter the list.",
  },
  {
    target: ".inspection-cycle-quick-filters",
    title: "Find the cycles you need",
    description:
      "Use quick filters to focus on your assignments, overdue cycles, upcoming due dates, or recently updated work.",
  },
  {
    target: '[data-onboarding-target="inspection"]',
    title: "Create an inspection cycle",
    description:
      "Select New Inspection Cycle to set its title, standard, owner, and due date. This action is available when you have permission.",
  },
  {
    target: ".inspection-cycle-page .tab-content",
    title: "Open a cycle",
    description:
      "Select a cycle number or title to view its engagements. Check the owner, due date, status, and progress, or use the three-dot menu for available actions.",
  },
  {
    target: ".inspection-cycle-refresh",
    title: "Refresh the list",
    description: "Select Refresh to reload the latest saved cycle records.",
  },
];

export const journeyPath = (cycleId, engagementId, stage = "preparation") =>
  engagementId
    ? `/inspections/${cycleId}/engagements/${engagementId}/${stage}`
    : cycleId
    ? `/inspections/${cycleId}/edit?step=engagements`
    : "/inspections/list";

export default function InspectionJourneyNav({
  cycleId,
  engagementId,
  stage = "cycles",
  preparationComplete = false,
  guidanceVariant = "inline",
  renderBelow,
  showSteps = false,
}) {
  const [selectionMessage, setSelectionMessage] = useState("");
  useEffect(() => setSelectionMessage(""), [cycleId, engagementId, stage]);

  const showSelectionMessage = (id, label) => {
    setSelectionMessage(
      id === "engagements"
        ? "Select an Inspection Cycle to view the Engagements."
        : !cycleId
        ? `Select an Inspection Cycle, then an Engagement to view ${label}.`
        : `Select an Engagement to view ${label}.`
    );
  };
  const items = [
    [
      "cycles",
      "Inspection Cycles",
      "/inspections/list",
      "Select or create cycle",
    ],
    [
      "engagements",
      "Engagements",
      cycleId && journeyPath(cycleId),
      "Import or create engagements",
    ],
    [
      "preparation",
      "Preparation",
      cycleId && engagementId && journeyPath(cycleId, engagementId),
      "Assign responsibilities",
    ],
    [
      "reviews",
      "Reviews",
      cycleId && engagementId && journeyPath(cycleId, engagementId, "reviews"),
      "Perform reviews",
    ],
    [
      "reporting",
      "Reporting",
      cycleId &&
        engagementId &&
        journeyPath(cycleId, engagementId, "reporting"),
      "Generate reports",
    ],
  ];
  const guidance = {
    cycles:
      "Create an inspection cycle to define its standard, owner, and due date, or use the quick filters to find an existing cycle. Select a cycle name or number to open its engagements and continue the inspection process.",
    engagements: cycleId
      ? "Create or import engagements for this inspection cycle, assign preparation responsibilities, and track their status. Select an engagement to complete its preparation or continue its reviews."
      : "",
    preparation:
      "Check the engagement details, team, key dates, risks, scope, checklists, and supporting documents. Complete the required information and resolve readiness issues before submitting for review. Preparation and review work can be assigned to different users.",
    reviews: engagementId
      ? "Open a review to work through its assigned checklists, respond to queries, record findings, and complete the required sign-offs. Continue the work assigned to you, then open Reporting to view the reports generated for this engagement."
      : "",
    reporting:
      "Preview or download the reports generated for this engagement. Open the associated review to check outstanding work and sign-offs; reports become available as the review submission and sign-off process progresses.",
  };
  const pageGuidance = guidance[stage] && (
    <section
      className={`inspection-journey-guidance inspection-journey-guidance--${guidanceVariant}`}
      aria-label="Help for this page"
    >
      {guidanceVariant === "sidebar" && (
        <header>
          <span>
            <i className="bx bx-bulb" aria-hidden="true" />
          </span>
          <div>
            <small>PAGE HELP</small>
            <h2>
              {items.find(([id]) => id === stage)?.[1] || "Inspection workflow"}
            </h2>
          </div>
        </header>
      )}
      <p>{guidance[stage]}</p>
      {guidanceVariant === "sidebar" && stage === "cycles" && (
        <>
          <div className="inspection-journey-guidance__details">
            <h3>Create and manage a cycle</h3>
            <p>
              Use New Inspection Cycle to enter the title, standard, owner, and
              due date. Use the row’s three-dot menu to preview details, view
              activities, or edit when permitted.
            </p>
            <h3>Continue to engagements</h3>
            <p>
              Select a cycle number or title to open its engagements. Create or
              import engagements there, then continue through preparation,
              reviews, and reporting.
            </p>
          </div>
          <section
            className="inspection-journey-guidance__tour"
            aria-label="Guided tour"
          >
            <h3>Take a Guided Tour</h3>
            <p>Walk through this screen to learn how it works.</p>
            <button
              type="button"
              onClick={() => startGuidedTour(cycleTourSteps)}
            >
              <i className="bx bx-play" aria-hidden="true" />
              Start Tour
            </button>
          </section>
        </>
      )}
    </section>
  );
  return (
    <>
      {showSteps && (
        <nav
          className="inspection-journey-nav"
          aria-label="Inspection workflow"
        >
          {items.map(([id, label, to, description], index) => {
            const content = (
              <>
                <span className="inspection-journey-nav__number">
                  {id === "preparation" && preparationComplete && stage !== id
                    ? "✓"
                    : index + 1}
                </span>
                <span className="inspection-journey-nav__copy">
                  <strong>{label}</strong>
                  <small>{description}</small>
                </span>
              </>
            );
            return to ? (
              <Link
                key={id}
                to={to}
                onClick={() => setSelectionMessage("")}
                className={stage === id ? "is-current" : ""}
                aria-current={stage === id ? "step" : undefined}
              >
                {content}
              </Link>
            ) : (
              <button
                key={id}
                type="button"
                className={stage === id ? "is-current" : ""}
                aria-current={stage === id ? "step" : undefined}
                onClick={() => showSelectionMessage(id, label)}
              >
                {content}
              </button>
            );
          })}
        </nav>
      )}
      <Modal
        centered
        isOpen={Boolean(selectionMessage)}
        toggle={() => setSelectionMessage("")}
        labelledBy="journey-selection-title"
        aria-describedby="journey-selection-description"
      >
        <ModalHeader
          id="journey-selection-title"
          toggle={() => setSelectionMessage("")}
        >
          Selection required
        </ModalHeader>
        <ModalBody id="journey-selection-description">
          {selectionMessage}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => setSelectionMessage("")}>
            Got it
          </Button>
        </ModalFooter>
      </Modal>
      {renderBelow ? renderBelow(pageGuidance) : pageGuidance}
    </>
  );
}
